// Copyright (c) Microsoft. All rights reserved.

import { Button, makeStyles, shorthands, Spinner, tokens } from '@fluentui/react-components';
import React from 'react';
import { ChatMessageType, IChatMessage } from '../../../libs/models/ChatMessage';
import { ChatHistoryItem } from './ChatHistoryItem';
import { Breakpoints } from '../../../styles';

const useClasses = makeStyles({
    root: {
        ...shorthands.gap(tokens.spacingVerticalM),
        display: 'flex',
        flexDirection: 'column',
        justifySelf: 'center',
        width: '100%',
        padding: '0 16px',
        boxSizing: 'border-box',
        ...Breakpoints.small({
            padding: '0 8px',
        }),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '200px',
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '200px',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
});

interface ChatHistoryProps {
    messages: IChatMessage[];
    messagesLoadState: 'notLoaded' | 'loading' | 'loaded';
    onRetry?: () => void;
    /** Attached to the last message group — used to anchor scroll behavior during streaming. */
    lastItemRef?: React.Ref<HTMLDivElement>;
}

/**
 * Groups consecutive messages from the same user sent within the same second
 * so they can be displayed in a single bubble.
 */
const GROUP_WINDOW_MS = 5000;

function groupMessages(messages: IChatMessage[]): IChatMessage[][] {
    const groups: IChatMessage[][] = [];
    for (const message of messages) {
        const lastGroup = groups[groups.length - 1] as IChatMessage[] | undefined;
        const lastMessage = lastGroup?.[lastGroup.length - 1];
        const isSameAuthor =
            lastGroup !== undefined &&
            lastGroup[0].userId === message.userId &&
            lastGroup[0].authorRole === message.authorRole;
        const isWithinWindow =
            lastMessage !== undefined &&
            new Date(message.timestamp).getTime() - new Date(lastMessage.timestamp).getTime() <= GROUP_WINDOW_MS;
        if (isSameAuthor && isWithinWindow) {
            lastGroup.push(message);
        } else {
            groups.push([message]);
        }
    }

    // Sort messages within each group: non-document messages first, document messages last
    groups.forEach((group) => {
        group.sort((a, b) => {
            const aIsDocument = a.type === ChatMessageType.Document ? 1 : 0;
            const bIsDocument = b.type === ChatMessageType.Document ? 1 : 0;
            return aIsDocument - bIsDocument;
        });
    });

    return groups;
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({ messages, messagesLoadState, onRetry, lastItemRef }) => {
    const classes = useClasses();

    // Show loading spinner when messagesLoadState is 'loading'
    if (messagesLoadState === 'loading') {
        return (
            <div className={classes.loadingContainer}>
                <Spinner label="Mesajlar yükleniyor..." />
            </div>
        );
    }

    // Show error state with retry button when notLoaded with no messages
    if (messagesLoadState === 'notLoaded' && messages.length === 0 && onRetry) {
        return (
            <div className={classes.errorContainer}>
                <div>Mesajlar yüklenemedi</div>
                <Button appearance="primary" onClick={onRetry}>
                    Tekrar dene
                </Button>
            </div>
        );
    }

    const grouped = groupMessages(messages);

    // Render messages when messagesLoadState is 'loaded'
    return (
        <div className={classes.root}>
            {grouped.map((group, index) => {
                const key = `${group[0].timestamp}-${index}`;
                const item = <ChatHistoryItem messages={group} messageIndex={index} />;
                // Wrap the last group so the scroll logic can measure where the most recent
                // (user) message sits relative to the viewport during streaming.
                return index === grouped.length - 1 ? (
                    // Flex column so the child's `align-self` (right-aligned user messages) is preserved.
                    <div key={key} ref={lastItemRef} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        {item}
                    </div>
                ) : (
                    <React.Fragment key={key}>{item}</React.Fragment>
                );
            })}
        </div>
    );
};

